import React, {
  ChangeEvent,
  CSSProperties,
  FC,
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import Label, { LabelAnimateState } from '../Label/Label';
import styles from './TextArea.module.css';

export interface TextAreaProps {
  id?: string;
  name?: string;
  disabled?: boolean;
  label?: string;
  animateLabel?: boolean;
  value?: string;
  placeholder?: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  minLength?: number;
  maxLength?: number;
  featureName?: string;
  className?: string;
  containerOverridesStyle?: CSSProperties;
}

const TextArea: FC<TextAreaProps> = ({
  id,
  name,
  disabled = false,
  label,
  animateLabel = false,
  value,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  minLength,
  maxLength,
  className = '',
  containerOverridesStyle,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [inputActive, setInputActive] = useState(Boolean(value));
  const [animateState, setAnimateState] = useState(LabelAnimateState.none);

  useEffect(() => {
    if (!animateLabel) return;
    setAnimateState(
      inputActive ? LabelAnimateState.active : LabelAnimateState.inactive,
    );
  }, [inputActive, animateLabel]);

  const {
    inputtextareasNoneDisabled: inputStyleDisabled,
    inputtextareasNoneFocus: inputStyleFocus,
    inputtextareasNoneNone: inputStyle,
  } = useTheme();

  const textareaStyle = {
    ...(disabled ? inputStyleDisabled : inputStyle),
    ...(inputActive ? inputStyleFocus : {}),
    ...containerOverridesStyle,
  };

  const classes = clsx({
    [styles.styledInput]: true,
    [styles.styledInputDisabled]: disabled,
    [styles.inputLabel]: !!label,
  });

  const containerClasses = clsx({
    [className]: true,
    [styles.container]: true,
  });

  return (
    <div className={containerClasses}>
      {/* TODO: if there is ever a component use case where the label does not animate,
        we will have to update this and `nth-block-label` to be styled for a static label,
        as this will just act like a placeholder and not a label.
        omitting static label case for now as there is no current design for it. */}
      {label && (
        <Label htmlFor={id} labelState={animateState}>
          {label}
        </Label>
      )}
      <textarea
        className={classes}
        id={id}
        name={name}
        disabled={disabled}
        value={inputValue}
        placeholder={placeholder}
        style={textareaStyle}
        onChange={(e): void => {
          setInputValue(e.target.value);
          if (onChange) onChange(e);
        }}
        onFocus={(): void => {
          setInputActive(true);
          if (onFocus) onFocus();
        }}
        onBlur={(): void => {
          setInputActive(Boolean(inputValue));
          if (onBlur) onBlur();
        }}
        minLength={minLength}
        maxLength={maxLength}
      />
    </div>
  );
};

export default TextArea;
